import { useCallback, useMemo } from "react"
import { Link } from "@tanstack/react-router"
import { IconCheckOutline18 as CheckIcon } from "nucleo-ui-outline-18"

import type { Project } from "@productlane/zero"
import { cn } from "@productlane/lib/src/cn"
import { Tooltip } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { useHighlights } from "@/components/editor/editor-config"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { useDetailLinkFactory } from "@/lib/detail-drawer"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero } from "@/lib/zero"
import { ProjectIconSelect } from "@/screens/app/projects/project-icon-select"
import { useThreadContext } from "../../thread-context"
import { LinkedItemActions } from "./shared/linked-item-actions"

export const ThreadProjectsList = () => {
	const { thread } = useThreadContext()
	const utils = api.useUtils()
	const detailLink = useDetailLinkFactory()
	const isCustomersEnabled = api.linear.customersEnabled.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})

	const removeMarks = useCallback((id: string) => {
		const marks = document.querySelectorAll(`mark[data-id="${id}"]`)
		marks.forEach((mark) => {
			mark.replaceWith(mark.innerHTML)
		})
	}, [])
	const zero = useZero()

	const projects = useMemo(() => thread.projects, [thread])
	const { hoveredIds, setHoveredIds } = useHighlights()

	const { role } = useRole()

	const handleRemoveLink = useCallback(
		async (projectId: string) => {
			const existingProjectIds = thread.projects.map((p) => p.id)
			if (existingProjectIds.includes(projectId)) {
				await mutateOk(
					zero.mutate(
						mutators.projects.unlink({
							feedbackId: thread.id,
							internalProjectId: projectId,
						}),
					),
				)
				await utils.thread.getThreadCustomerNeeds.invalidate()
				removeMarks(projectId)
			}
		},
		[thread, zero, utils, removeMarks],
	)

	const handleToggleImportant = useCallback(
		async (projectId: string, isImportant: boolean) => {
			if (role === "VIEWER") {
				return
			}

			if (!isCustomersEnabled.data && !isCustomersEnabled.isLoading) {
				toast.info({
					title: "Linear customers are disabled",
					description:
						"It seems that Customers in Linear are disabled or your plan is outdated. Please enable them in Linear's settings or upgrade your plan to avoid issues with creating customers.",
					action: {
						label: "Open Linear settings",
						callback: () =>
							window.open(
								"https://linear.app/settings/customers-requests",
								"_blank",
							),
					},
				})
			}

			await mutateOk(
				zero.mutate(
					mutators.projects.linkUpdate({
						projectId,
						feedbackId: thread.id,
						priority: Number(isImportant),
					}),
				),
			)
		},
		[role, isCustomersEnabled, zero, thread.id],
	)

	const insightProjects: (Project & { isImportant: boolean })[] =
		useMemo(() => {
			if (!(thread && projects && projects.length > 0)) {
				return []
			}
			return projects
				.filter((t) => thread.projects.map((p) => p.id).includes(t.id))
				.map((project) => ({
					...project,
					isImportant: project.customerNeeds.some(
						(cn) => cn.priority === 1 && cn.feedbackId === thread.id,
					),
				}))
		}, [thread, projects])

	return (
		<>
			{insightProjects.map((project) => (
				<div
					key={project.id}
					onMouseEnter={() => {
						setHoveredIds([project.id])
					}}
					onMouseLeave={() => {
						setHoveredIds([])
					}}
					className={cn(
						"group relative mt-1.5 select-none overflow-hidden rounded-lg border border-gray-200 bg-white/50 p-2 text-sm hover:bg-white dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-800",
						hoveredIds.includes(project.id) &&
							"border-gray-300 dark:border-gray-600",
					)}
				>
					<div
						className={cn(
							"flex items-center gap-2",
							project.isImportant ? "pr-14" : "pr-7",
						)}
					>
						<ProjectIconSelect
							project={project}
							className=""
							iconSize="default"
						/>
						<Link
							to={detailLink("project", project.id).to}
							className="flex min-w-0 flex-1 items-center gap-2"
							onClick={(e) => {
								e.stopPropagation()
								detailLink("project", project.id).onClick(e)
							}}
							onKeyDown={(e) => {
								e.stopPropagation()
							}}
						>
							<Tooltip label={project.name}>
								<div className="flex min-w-0 flex-row gap-2">
									<div className="max-w-52 flex-1 truncate font-medium">
										{project.name}
									</div>
									{project.state === "completed" && (
										<div className="flex flex-none flex-row items-center gap-1 group-hover:opacity-0">
											<div className="text-xs text-secondary">Completed</div>
											<CheckIcon className="h-3 w-3 flex-none text-tertiary" />
										</div>
									)}
								</div>
							</Tooltip>
						</Link>
					</div>

					<LinkedItemActions
						itemId={project.id}
						itemType="project"
						linearUrl={project.url}
						label={project.name}
						isRoadmapVisible={project.isVisible === true}
						isImportant={project.isImportant}
						onRemoveLink={handleRemoveLink}
						onToggleImportant={handleToggleImportant}
					/>
				</div>
			))}
		</>
	)
}
